import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiEdit2, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { fetchAgents } from '../api/agentService';
import { useToast } from '../components/Toast';
import {
  activateAssignmentRule,
  createAssignmentRule,
  deactivateAssignmentRule,
  deleteAssignmentRule,
  fetchAssignmentRules,
  updateAssignmentRule,
  updateAssignmentRulePriority,
} from '../api/assignmentRulesService';
import { fetchQueues } from '../api/queueService';

const METHOD_OPTIONS = [
  { value: 'direct', label: 'Direct' },
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'queue', label: 'Queue' },
];

function RuleModal({ agents, onClose, onSaved, queues, rule }) {
  const toast = useToast();
  const isEdit = Boolean(rule?.id);
  const [form, setForm] = useState({
    name: rule?.name || '',
    description: rule?.description || '',
    priority: rule?.priority ?? 1,
    is_active: rule?.is_active ?? true,
    assignment_method: rule?.assignment_method || 'direct',
    target_agent_id: rule?.target_agent_id || '',
    target_agents: rule?.target_agents || rule?.agent_pool || [],
    target_queue_id: rule?.target_queue_id || '',
    criteriaText: JSON.stringify(rule?.criteria || [], null, 2),
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (event) => {
    const { checked, name, type, value } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const toggleRoundRobinAgent = (agentId) => {
    setForm((current) => ({
      ...current,
      target_agents: current.target_agents.includes(agentId)
        ? current.target_agents.filter((id) => id !== agentId)
        : [...current.target_agents, agentId],
    }));
  };

  const buildPayload = () => {
    let criteria = [];

    try {
      criteria = JSON.parse(form.criteriaText || '[]');
    } catch {
      throw new Error('Criteria must be valid JSON.');
    }

    if (!Array.isArray(criteria)) {
      throw new Error('Criteria must be a JSON array.');
    }

    const payload = {
      name: form.name.trim(),
      description: form.description.trim(),
      priority: Number(form.priority),
      is_active: form.is_active,
      assignment_method: form.assignment_method,
      criteria,
    };

    if (!payload.name) {
      throw new Error('Rule name is required.');
    }

    if (form.assignment_method === 'direct') {
      if (!form.target_agent_id) {
        throw new Error('Select a target agent for direct assignment.');
      }
      payload.target_agent_id = form.target_agent_id;
    }

    if (form.assignment_method === 'round_robin') {
      if (!form.target_agents.length) {
        throw new Error('Select at least one agent for round robin.');
      }
      payload.agent_pool = form.target_agents;
      payload.target_agents = form.target_agents;
    }

    if (form.assignment_method === 'queue') {
      if (!form.target_queue_id) {
        throw new Error('Select a target queue.');
      }
      payload.target_queue_id = form.target_queue_id;
    }

    return payload;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const payload = buildPayload();
      if (isEdit) {
        await updateAssignmentRule(rule.id, payload);
      } else {
        await createAssignmentRule(payload);
      }
      onSaved();
    } catch (err) {
      toast.error(err?.message || err?.response?.data?.error || err?.response?.data?.message || 'Saving rule failed.');
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <h3>{isEdit ? 'Edit Assignment Rule' : 'Create Assignment Rule'}</h3>
          <button className="modal-close" type="button" onClick={onClose} aria-label="Close">x</button>
        </div>

        <form className="stack-form" onSubmit={onSubmit}>
          <label>
            <span>Name</span>
            <input name="name" value={form.name} onChange={onChange} />
          </label>

          <label>
            <span>Description</span>
            <textarea name="description" rows="3" value={form.description} onChange={onChange} />
          </label>

          <div className="form-row">
            <label>
              <span>Priority</span>
              <input name="priority" type="number" min="1" value={form.priority} onChange={onChange} />
            </label>
            <label>
              <span>Assignment Method</span>
              <select name="assignment_method" value={form.assignment_method} onChange={onChange}>
                {METHOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="toggle-field">
            <span className="toggle-field-label">Active</span>
            <label className="toggle-switch">
              <input name="is_active" type="checkbox" checked={form.is_active} onChange={onChange} />
              <span className="toggle-track"><span className="toggle-thumb" /></span>
            </label>
          </div>

          {form.assignment_method === 'direct' ? (
            <label>
              <span>Target Agent</span>
              <select name="target_agent_id" value={form.target_agent_id} onChange={onChange}>
                <option value="">Select agent</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>{agent.name}</option>
                ))}
              </select>
            </label>
          ) : null}

          {form.assignment_method === 'round_robin' ? (
            <div>
              <span style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Target Agents</span>
              <div style={{ display: 'grid', gap: 8, maxHeight: 180, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 10, padding: 12 }}>
                {agents.map((agent) => (
                  <label key={agent.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 500 }}>
                    <input
                      type="checkbox"
                      checked={form.target_agents.includes(agent.id)}
                      onChange={() => toggleRoundRobinAgent(agent.id)}
                    />
                    <span>{agent.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : null}

          {form.assignment_method === 'queue' ? (
            <label>
              <span>Target Queue</span>
              <select name="target_queue_id" value={form.target_queue_id} onChange={onChange}>
                <option value="">Select queue</option>
                {queues.map((queue) => (
                  <option key={queue.id} value={queue.id}>{queue.name}</option>
                ))}
              </select>
            </label>
          ) : null}

          <label>
            <span>Criteria JSON</span>
            <textarea
              name="criteriaText"
              rows="8"
              value={form.criteriaText}
              onChange={onChange}
              placeholder='[{"field":"lead_score","operator":"gte","value":70}]'
              style={{ minHeight: 170, paddingTop: 10, paddingBottom: 10 }}
            />
          </label>

          <div className="form-actions">
            <button className="primary-btn" type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Rule'}
            </button>
            <button className="ghost-btn" type="button" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AssignmentRulesPage() {
  const toast = useToast();
  const [rules, setRules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [queues, setQueues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingRule, setEditingRule] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [methodFilter, setMethodFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [priorityDrafts, setPriorityDrafts] = useState({});

  const loadPageData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (methodFilter) {
        params.assignment_method = methodFilter;
      }
      if (activeFilter) {
        params.is_active = activeFilter;
      }

      const [rulesResponse, agentsResponse, queuesResponse] = await Promise.all([
        fetchAssignmentRules(params),
        fetchAgents(),
        fetchQueues(),
      ]);

      const nextRules = rulesResponse.rules || rulesResponse.data?.rules || [];
      setRules(nextRules);
      setAgents(agentsResponse.data || agentsResponse.agents || []);
      setQueues(queuesResponse.queues || queuesResponse.data?.queues || []);
      setPriorityDrafts(
        nextRules.reduce((accumulator, rule) => {
          accumulator[rule.id] = String(rule.priority ?? '');
          return accumulator;
        }, {})
      );
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to load assignment rules.');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, methodFilter, toast]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  const sortedRules = useMemo(
    () => [...rules].sort((left, right) => (left.priority ?? 0) - (right.priority ?? 0)),
    [rules]
  );

  const openCreateModal = () => {
    setEditingRule(null);
    setShowModal(true);
  };

  const openEditModal = (rule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Delete this assignment rule?')) {
      return;
    }

    try {
      await deleteAssignmentRule(ruleId);
      toast.success('Assignment rule deleted.');
      await loadPageData();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to delete assignment rule.');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      if (rule.is_active) {
        await deactivateAssignmentRule(rule.id);
      } else {
        await activateAssignmentRule(rule.id);
      }
      toast.success(`Rule ${rule.is_active ? 'deactivated' : 'activated'}.`);
      await loadPageData();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to update rule status.');
    }
  };

  const handlePrioritySave = async (ruleId) => {
    try {
      await updateAssignmentRulePriority(ruleId, Number(priorityDrafts[ruleId] || 1));
      toast.success('Rule priority updated.');
      await loadPageData();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.response?.data?.message || 'Unable to update rule priority.');
    }
  };

  const getTargetLabel = (rule) => {
    if (rule.assignment_method === 'direct') {
      return agents.find((agent) => agent.id === rule.target_agent_id)?.name || rule.target_agent_id || '—';
    }
    if (rule.assignment_method === 'round_robin') {
      return `${(rule.target_agents || rule.agent_pool || []).length} agents`;
    }
    if (rule.assignment_method === 'queue') {
      return queues.find((queue) => queue.id === rule.target_queue_id)?.name || rule.target_queue_id || '—';
    }
    return '—';
  };

  return (
    <div>
      <div className="page-title-row">
        <div>
          <h2>Assignment Rules</h2>
          <p style={{ margin: '6px 0 0', color: '#64748b' }}>
            Configure rule priority, matching criteria, and assignment targets for the routing engine.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="ghost-btn" type="button" onClick={loadPageData}>
            <FiRefreshCw style={{ marginRight: 6 }} /> Refresh
          </button>
          <button className="primary-btn" type="button" onClick={openCreateModal}>New Rule</button>
        </div>
      </div>

      <div className="filter-bar">
        <select className="filter-select" value={methodFilter} onChange={(event) => setMethodFilter(event.target.value)}>
          <option value="">All Methods</option>
          {METHOD_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select className="filter-select" value={activeFilter} onChange={(event) => setActiveFilter(event.target.value)}>
          <option value="">All States</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {loading ? <p>Loading rules…</p> : null}

      {!loading ? (
        <table className="data-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Priority</th>
              <th>Method</th>
              <th>Target</th>
              <th>Criteria</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRules.length ? (
              sortedRules.map((rule) => (
                <tr key={rule.id}>
                  <td>
                    <strong style={{ color: '#0f172a' }}>{rule.name}</strong>
                    {rule.description ? <div style={{ color: '#64748b', fontSize: 13 }}>{rule.description}</div> : null}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input
                        type="number"
                        min="1"
                        value={priorityDrafts[rule.id] || ''}
                        onChange={(event) => setPriorityDrafts((current) => ({ ...current, [rule.id]: event.target.value }))}
                        style={{ width: 74 }}
                      />
                      <button className="ghost-btn" type="button" onClick={() => handlePrioritySave(rule.id)}>Save</button>
                    </div>
                  </td>
                  <td>{METHOD_OPTIONS.find((option) => option.value === rule.assignment_method)?.label || rule.assignment_method}</td>
                  <td>{getTargetLabel(rule)}</td>
                  <td>{Array.isArray(rule.criteria) ? rule.criteria.length : 0} conditions</td>
                  <td>
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 10px',
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 600,
                        background: rule.is_active ? '#dcfce7' : '#e2e8f0',
                        color: rule.is_active ? '#166534' : '#475569',
                      }}
                    >
                      {rule.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button className="icon-action-btn" type="button" title="Edit rule" onClick={() => openEditModal(rule)}>
                        <FiEdit2 />
                      </button>
                      <button className="icon-action-btn" type="button" title="Toggle rule state" onClick={() => handleToggleActive(rule)}>
                        <FiRefreshCw />
                      </button>
                      <button className="icon-action-btn icon-action-btn--danger" type="button" title="Delete rule" onClick={() => handleDelete(rule.id)}>
                        <FiTrash2 />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7">No assignment rules found.</td></tr>
            )}
          </tbody>
        </table>
      ) : null}

      {showModal ? (
        <RuleModal
          agents={agents}
          queues={queues}
          rule={editingRule}
          onClose={() => setShowModal(false)}
          onSaved={async () => {
            setShowModal(false);
            setEditingRule(null);
            toast.success(editingRule ? 'Assignment rule updated.' : 'Assignment rule created.');
            await loadPageData();
          }}
        />
      ) : null}
    </div>
  );
}

export default AssignmentRulesPage;
